import { CanvasElement } from '../types';

// A serial element only counts as "the user actually set this up" if its
// settings are well-formed — otherwise it's a stray/default element that
// was never configured, and should stay hidden everywhere (preview and
// PDF) instead of rendering garbled output like "[то]" in exports.
export function isValidSerialElement(el: any): boolean {
  if (el?.type !== 'serial') return false;
  const s = el.serialSettings;
  if (!s) return false;
  if (s.manualMode) {
    return typeof s.manualValue === 'string' && s.manualValue.trim().length > 0;
  }
  const digitsOk = typeof s.digits === 'number' && s.digits > 0;
  const currentOk = typeof s.current === 'number' && !isNaN(s.current);
  const prefixOk = typeof s.prefix === 'string';
  const suffixOk = typeof s.suffix === 'string';
  return digitsOk && currentOk && prefixOk && suffixOk;
}

// Shared between App.tsx (main canvas PDF export) and QuickInvoiceView.tsx
// (Quick Invoice PDF export + WhatsApp send). Previously duplicated in
// both files — a fix applied to one copy silently didn't apply to the
// other, which is how the QR/label pairing bug survived in Quick Invoice
// after already being fixed in App.tsx. Single source of truth now.

// PDF export's page-break push: groups a QR/stamp/signature with a text
// caption sitting directly beneath it so the pair moves together across
// a page boundary instead of the caption getting orphaned onto the next
// page alone while its QR stays behind.
export function computeGroupedPageShifts(
  elements: CanvasElement[],
  pageHeight: number
): Map<string, number> {
  const nonTable = elements.filter((el: any) => el.type !== 'table');
  const used = new Set<string>();
  const groups: { ids: string[]; top: number; bottom: number }[] = [];

  const sortedByY = [...nonTable].sort((a: any, b: any) => (a.y || 0) - (b.y || 0));

  sortedByY.forEach((el: any) => {
    if (used.has(el.id)) return;
    const top = el.y || 0;
    let bottom = top + (el.height || 0);
    const ids = [el.id];
    used.add(el.id);

    if (el.type === 'qr' || el.type === 'stamp' || el.type === 'signature') {
      // PRIMARY: explicit, authored link — set once on the template and
      // preserved through every clone/spread. Cannot misfire regardless
      // of position, push order, or text content.
      let caption = el.captionId
        ? nonTable.find((e: any) => e.id === el.captionId && !used.has(e.id))
        : undefined;

      // FALLBACK: old proximity guess, only for elements/templates that
      // predate captionId. Keeps existing behavior unchanged for them —
      // no better, no worse than before this change.
      if (!caption) {
        caption = sortedByY.find((other: any) =>
          !used.has(other.id) &&
          other.type === 'text' &&
          (other.y || 0) >= bottom - 2 &&
          (other.y || 0) <= bottom + 20 &&
          Math.abs((other.x || 0) - (el.x || 0)) < (el.width || 0) + 40
        );
      }

      if (caption) {
        used.add(caption.id);
        ids.push(caption.id);
        bottom = Math.max(bottom, (caption.y || 0) + (caption.height || 0));
      }
    }
    groups.push({ ids, top, bottom });
  });

  groups.sort((a, b) => a.top - b.top);

  // Tracks the bottom edge of the previously PLACED group (after its own
  // shift, if any) — used only to prevent this group from overlapping it.
  // This is intentionally NOT a cumulative running total: an earlier
  // group's big page-break jump should not automatically become baked
  // into every group that follows it. Each group's shift is derived from
  // its own boundary math first; the previous group's placement is only
  // consulted afterward, as a floor, to stop two groups landing on top
  // of each other.
 
  const shifts = new Map<string, number>();

  // Track placed groups (with their x-range) so we only push a group down
  // when it actually overlaps a previous one horizontally too — comparing
  // y-ranges alone falsely flags side-by-side elements (e.g. a logo on
  // the left and an invoice-details box on the right at similar heights)
  // as colliding, cascading bogus downward shifts through the rest of
  // the layout.
  const placedGroups: { left: number; right: number; bottom: number }[] = [];

  groups.forEach(group => {
    const groupEls = group.ids.map(id => elements.find((e: any) => e.id === id)).filter(Boolean) as any[];
    const left = Math.min(...groupEls.map(e => e.x || 0));
    const right = Math.max(...groupEls.map(e => (e.x || 0) + (e.width || 0)));

    let shift = 0;
    for (const placed of placedGroups) {
      const horizontallyOverlaps = left < placed.right && right > placed.left;
      if (horizontallyOverlaps && group.top + shift < placed.bottom) {
        shift = Math.max(shift, placed.bottom - group.top);
      }
    }

    let top = group.top + shift;
    const height = group.bottom - group.top;
    let bottom = top + height;

    const pageOfTop = Math.floor(top / pageHeight);
    const pageOfBottom = Math.floor((bottom - 0.01) / pageHeight);
    if (height > 0 && pageOfTop !== pageOfBottom) {
      // A PDF page break is a hard physical cut — even a 1-2px sliver
      // crossing it still forces the renderer to emit a whole extra
      // page to hold that sliver, leaving a near-empty trailing page
      // behind. The old 20% threshold only decided how MUCH of the
      // block got left behind on that stray page, not whether the
      // stray page happened at all — so it didn't actually prevent the
      // problem it was meant to prevent. Always push the whole group
      // to the next page instead; worst case is a bit of extra
      // whitespace at the bottom of the previous page, which beats a
      // near-blank trailing page every time.
      const nextPageTop = (pageOfTop + 1) * pageHeight;
      const extraShift = nextPageTop - top;
      shift += extraShift;
      top += extraShift;
      bottom += extraShift;
    } else if (pageOfTop > 0) {
      // This group already sits fully on a later page (page 2, 3, 4...).
      // If it's landing far below that page's top edge with nothing else
      // above it on the same page, that gap is just leftover padding
      // from earlier height estimates — snap it up closer to the top of
      // its page instead of leaving a big empty gap above it.
      const pageTop = pageOfTop * pageHeight;
      const distanceFromPageTop = top - pageTop;
      const SNAP_THRESHOLD = 300; // only snap if the gap looks accidental
      if (distanceFromPageTop > SNAP_THRESHOLD) {
        const snapAmount = distanceFromPageTop - 40; // leave a small 40px margin
        shift -= snapAmount;
        top -= snapAmount;
        bottom -= snapAmount;
      }
    }

    group.ids.forEach(id => shifts.set(id, shift));
// Only register groups anchored by a QR/stamp/signature as obstacles for
// later groups — not every group. This fixes QR2 jumping ahead of NOTES
// (a QR/stamp/signature-specific bug) without touching plain text/header
// groups, which never needed to be treated as obstacles in the first place.
const isQrGroup = groupEls.some(
  (e: any) => e.type === 'qr' || e.type === 'stamp' || e.type === 'signature'
);
if (isQrGroup) {
  placedGroups.push({ left, right, bottom });
}
  });

  return shifts;
}

// Backend _auto elements (QR + its label) carry a fixed y computed
// server-side, independent of this invoice's actual dynamic table height.
// Repositioning them here — relative to the real content bottom — is what
// keeps the QR glued to the content instead of leaving a gap. Stacks each
// _auto QR "group" (invoice QR + its label, tracking QR + its label)
// independently below the real content, in the order given by
// groupOffsets, each offset by its own per-type nudge.
//
// Does NOT trust the backend's raw relative y-order within a group — the
// backend sometimes emits the label with a y ABOVE its QR's y, and a naive
// uniform shift used to preserve that, rendering the caption above the QR
// instead of below it. Explicitly re-stacks: QR/stamp/signature first,
// its label directly beneath, regardless of the order or raw y values the
// backend sent.
export function repositionAutoGroups(
  baseElements: any[],
  autoElements: any[],
  groupOffsets: Record<string, { x: number; y: number }>,
  gap = 2,
  pageWidth?: number
) {
  if (autoElements.length === 0) return autoElements;
  const contentBottom = baseElements.reduce(
    (max, el) => Math.max(max, (el.y || 0) + (el.height || 0)), 0
  );
  // Never trust an offset blindly — a stale or zoom-mismatched drag
  // offset (see the fix in QuickInvoiceView's onMove handler) can push an
  // element's x off the visible page entirely. Clamp every resulting x
  // back onto the page whenever a pageWidth is supplied.
  const clampX = (x: number, width: number) =>
    pageWidth ? Math.min(Math.max(0, x), Math.max(0, pageWidth - width)) : x;
  const groups: Record<string, any[]> = {};
  autoElements.forEach(el => {
    const key = String(el._auto || '').replace('_label', '');
    groups[key] = groups[key] || [];
    groups[key].push(el);
  });

  

  let cursor = contentBottom + gap;
  const result: any[] = [];
  Object.keys(groups).forEach(key => {
    const groupEls = groups[key];
    const offset = groupOffsets[key] || { x: 0, y: 0 };
    const anchor = groupEls.find((el: any) => el.type === 'qr' || el.type === 'stamp' || el.type === 'signature') || groupEls[0];
    const label = groupEls.find((el: any) => el !== anchor);

    const anchorY = cursor + offset.y;
    // Backend _auto elements sometimes arrive with no id (or the same
    // missing id on both the QR and its label). Every downstream step in
    // handleExportPDF — clampedById, captionByAnchorIdClamp,
    // pageBreakShifts — is a Map keyed by el.id. Two elements sharing an
    // undefined id collide in those Maps, and whichever was inserted last
    // (the label) silently overwrites the QR's entry — so both end up
    // rendered at the same y. Force a stable, unique id here so nothing
    // downstream can collide.
    const anchorId = anchor.id || `auto-${key}`;
    const labelId = label ? (label.id || `auto-${key}-label`) : undefined;

    // A saved anchor (especially a backend-generated 'image' QR element)
    // can arrive with no height set at all — trusting it here collapses
    // the label's y to anchorY + 4, landing the caption right on top of
    // the QR instead of below it. QR/stamp/signature elements are always
    // rendered at a real pixel size somewhere downstream, so fall back to
    // width (or a sane default) rather than trusting a missing/zero height.
    const anchorHeight = anchor.height || anchor.width || 100;

    result.push({
      ...anchor,
      id: anchorId,
      y: anchorY,
      height: anchorHeight,
      x: clampX((anchor.x || 0) + offset.x, anchor.width || 0),
      captionId: labelId,
    });

    let bottom = anchorY + anchorHeight;
    if (label) {
      const labelY = anchorY + anchorHeight + 4;
      result.push({
        ...label,
        id: labelId,
        y: labelY,
        x: clampX((label.x || 0) + offset.x, label.width || 0),
      });
      bottom = labelY + (label.height || 0);
    }
    cursor = bottom + gap;
  });
  return result;
}

// Final safety pass, run AFTER all other y-adjustments (table-growth push,
// page-break shifts). Guarantees zero overlap among everything below the
// table, no matter how long the table grows. Groups a QR/stamp/signature
// with its caption (same pairing rule as computeGroupedPageShifts, so a
// pair is never torn apart), then cascades top-to-bottom: each group's top
// is floored to at least `gap` below the bottom of whatever's directly
// above it (the table, or the previous group in the stack).
//
// This is a floor, not a repositioning — a group already comfortably
// spaced below its neighbour is left untouched (shift stays 0). It never
// moves anything UP, so it can't collapse deliberate whitespace; it only
// ever pushes down far enough to guarantee separation. Because `cursor`
// always tracks the real, already-shifted bottom of the previous group
// (not the group's original authored position), this holds regardless of
// how many rows the table has.
export function enforceNoOverlapBelowTable(
  elements: any[],
  tableBottom: number,
  gap?: number,
  pageWidth?: number
): Map<string, number> {
  // Default gap was a flat 24px tuned for A4 — fine there, but on a
  // 220-302px thermal receipt it forcibly re-inflates spacing that
  // QuickInvoiceView already computed correctly at ~8-10px, producing a
  // large stray gap between every group (Amount in Words, Notes, QR,
  // etc). Scale the floor down for narrower pages instead; A4/Letter/
  // Legal keep the original 24px behavior unchanged.
  if (gap === undefined) {
    gap = pageWidth && pageWidth <= 220 ? 8
        : pageWidth && pageWidth <= 302 ? 10
        : 24;
  }
  const nonTable = elements.filter((el: any) => el.type !== 'table');
  const used = new Set<string>();
  const groups: { ids: string[]; top: number; bottom: number }[] = [];

  const sortedByY = [...nonTable].sort((a: any, b: any) => (a.y || 0) - (b.y || 0));

  sortedByY.forEach((el: any) => {
    if (used.has(el.id)) return;
    const top = el.y || 0;
    let bottom = top + (el.height || 0);
    const ids = [el.id];
    used.add(el.id);

    if (el.type === 'qr' || el.type === 'stamp' || el.type === 'signature') {
      let caption = el.captionId
        ? nonTable.find((e: any) => e.id === el.captionId && !used.has(e.id))
        : undefined;
      if (!caption) {
        caption = sortedByY.find((other: any) =>
          !used.has(other.id) &&
          other.type === 'text' &&
          (other.y || 0) >= bottom - 2 &&
          (other.y || 0) <= bottom + 20 &&
          Math.abs((other.x || 0) - (el.x || 0)) < (el.width || 0) + 40
        );
      }
      if (caption) {
        used.add(caption.id);
        ids.push(caption.id);
        bottom = Math.max(bottom, (caption.y || 0) + (caption.height || 0));
      }
    }
    groups.push({ ids, top, bottom });
  });

  groups.sort((a, b) => a.top - b.top);

  const shifts = new Map<string, number>();

  // Rectangle-aware placement: track every already-placed group's full
  // bounding box (left, right, bottom) — not just a single running
  // cursor. A group only gets pushed down if it ACTUALLY overlaps a
  // previously placed group both vertically AND horizontally. Two
  // groups sitting side-by-side (e.g. Notes on the left, QR on the
  // right) are left exactly where they are; only genuine collisions
  // get resolved. This works identically no matter how many groups,
  // rows, or pages exist, because it's driven by real geometry, not a
  // fixed column assumption.
  const placed: { left: number; right: number; bottom: number }[] = [];

  groups.forEach(group => {
    const groupEls = group.ids
      .map(id => elements.find((e: any) => e.id === id))
      .filter(Boolean) as any[];
    const left = Math.min(...groupEls.map(e => e.x || 0));
    const right = Math.max(...groupEls.map(e => (e.x || 0) + (e.width || 0)));
    const groupHeight = group.bottom - group.top;

    // Always respect the table's own bottom boundary as a floor —
    // nothing below the table should ever creep up above it.
    let shift = group.top < tableBottom + gap ? (tableBottom + gap) - group.top : 0;

    for (const p of placed) {
      const proposedTop = group.top + shift;
      if (proposedTop < p.bottom + gap) {
        shift = Math.max(shift, (p.bottom + gap) - group.top);
      }
    }

    group.ids.forEach(id => shifts.set(id, shift));
    placed.push({ left, right, bottom: group.top + shift + groupHeight });
  });

  return shifts;
}